import { ClipboardList, Pencil, Paperclip, Truck, User, Route as RouteIcon, MapPin } from 'lucide-react';
import type { LogbookEntry } from '../../types/logbook';
import { colorDeTipo, esNota, fueCorregida } from '../../utils/logbook';
import { formatDateTime } from '../../utils/trips';

/**
 * FE-13 — el timeline de novedades. La lectura natural de las novedades es
 * cronológica, no tabular (ADR-015): lo que se busca aquí es "qué pasó", y una
 * tabla con ocho columnas obliga a reconstruir mentalmente el relato.
 *
 * Se usa en dos sitios con la MISMA forma y dos órdenes distintos, que es
 * información y no capricho:
 *  - En la página de Novedades, **la más reciente primero** (lo último que pasó).
 *  - En el expediente de un viaje, **la más antigua primero** — ahí se lee el
 *    viaje como una historia, de la salida a la llegada.
 *
 * El orden lo decide quien llama pasando la lista ya ordenada. Este componente
 * no reordena: si lo hiciera, una de las dos superficies estaría mintiendo.
 */
export function LogbookTimeline({
  entries,
  onSelect,
  vacio = 'Sin novedades registradas en este periodo.',
}: {
  entries: LogbookEntry[];
  onSelect?: (entry: LogbookEntry) => void;
  vacio?: string;
}) {
  if (entries.length === 0) {
    return (
      <div style={{ padding: '2rem 1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}>
        {vacio}
      </div>
    );
  }

  return (
    <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {entries.map(entry => {
        const color = colorDeTipo(entry.entryTypeCode);
        const corregida = fueCorregida(entry);
        return (
          <li key={entry.id}>
            <button
              type="button"
              onClick={() => onSelect?.(entry)}
              disabled={!onSelect}
              className="logbook-entry"
              style={{ borderLeftColor: color, cursor: onSelect ? 'pointer' : 'default' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                <span
                  style={{
                    fontSize: '11px', fontWeight: 800, color,
                    background: `${color}1F`, border: `1px solid ${color}59`,
                    borderRadius: '99px', padding: '2px 9px',
                  }}
                >
                  {esNota(entry) ? '📝 ' : ''}{entry.entryTypeLabel}
                </span>
                {corregida && (
                  <span
                    title="Esta novedad fue corregida — sus cambios tienen el de qué a qué"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '3px',
                      fontSize: '10px', fontWeight: 800, color: '#F59E0B',
                      background: 'rgba(245,158,11,0.14)', border: '1px solid rgba(245,158,11,0.35)',
                      borderRadius: '99px', padding: '1px 7px',
                    }}
                  >
                    <Pencil size={9} /> CORREGIDA
                  </span>
                )}
                {/* FE-30. Es un CONTEO, no las fotos: el contrato manda
                    `attachmentCount` justo para poder pintar esto sin cargar
                    200 objetos en una página de 50 novedades. */}
                {!!entry.attachmentCount && entry.attachmentCount > 0 && (
                  <span
                    title={entry.attachmentCount === 1 ? '1 foto adjunta' : `${entry.attachmentCount} fotos adjuntas`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: '3px',
                      fontSize: '10px', fontWeight: 800, color: 'var(--text-secondary)',
                      background: 'var(--bg-raised)', border: '1px solid var(--border)',
                      borderRadius: '99px', padding: '1px 7px',
                    }}
                  >
                    <Paperclip size={9} /> {entry.attachmentCount}
                  </span>
                )}
                <span style={{ marginLeft: 'auto', fontSize: '11px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                  {formatDateTime(entry.occurredAt)}
                </span>
              </div>

              <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.5, textAlign: 'left' }}>
                {entry.description}
              </p>

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', fontSize: '11px', color: 'var(--text-muted)' }}>
                {entry.vehiclePlate && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    <Truck size={11} /> {entry.vehiclePlate}
                  </span>
                )}
                {entry.driverName && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    <User size={11} /> {entry.driverName}
                  </span>
                )}
                {/* La novedad puede colgar de un viaje DECLARADO por alguien.
                    Que no lo tenga no significa que esté fuera del expediente
                    de ningún viaje: la ventana la mete sola (ADR-024). */}
                {entry.tripId && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }} title="Asignada explícitamente a un viaje">
                    <RouteIcon size={11} /> viaje asignado
                  </span>
                )}
                {!entry.vehiclePlate && !entry.driverName && !entry.tripId && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px' }}>
                    <MapPin size={11} /> sin asociación visible
                  </span>
                )}
              </div>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export function LogbookTimelineHeader({ total }: { total: number }) {
  return (
    <h4 style={{ margin: 0, fontSize: '13px', fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
      <ClipboardList size={15} style={{ color: 'var(--accent)' }} />
      Novedades ({total})
    </h4>
  );
}
